// ReportDownload - Génération et téléchargement du rapport de conformité HTML (côté client)

import { useRef } from 'react';
import type { AnalyseResult } from '@/pages/analysis/engine';

interface ReportDownloadProps {
  rapportNumber: string;
  result: AnalyseResult;
}

export default function ReportDownload({ rapportNumber, result }: ReportDownloadProps) {
  const downloadingRef = useRef(false);

  const generateReportHTML = (): string => {
    const now = new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const risqueColor = result.risque.couleur === 'green' ? '#16a34a' : result.risque.couleur === 'amber' ? '#d97706' : '#dc2626';

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Rapport de conformité NGAP — ${rapportNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', 'Segoe UI', sans-serif; font-size: 11px; color: #1a1a1a; line-height: 1.6; padding: 40px; }
    h1 { font-family: 'Playfair Display', Georgia, serif; font-size: 22px; color: #2d6a4f; margin-bottom: 4px; }
    h2 { font-family: 'Playfair Display', Georgia, serif; font-size: 15px; color: #2d6a4f; margin-top: 24px; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
    h3 { font-size: 12px; color: #555; font-weight: 600; margin-bottom: 6px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
    .header-right { text-align: right; font-size: 10px; color: #777; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
    .badge-green { background: #dcfce7; color: #166534; }
    .badge-amber { background: #fef3c7; color: #92400e; }
    .badge-red { background: #fee2e2; color: #991b1b; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th { text-align: left; font-size: 9px; color: #777; text-transform: uppercase; font-weight: 600; border-bottom: 1px solid #e5e7eb; padding: 6px 4px; letter-spacing: 0.5px; }
    td { padding: 6px 4px; border-bottom: 1px solid #f3f4f6; font-size: 10px; }
    .rule-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; margin-bottom: 8px; }
    .rule-ok { border-left: 3px solid #16a34a; }
    .rule-warn { border-left: 3px solid #d97706; }
    .total-row { font-size: 13px; font-weight: 700; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 8px; color: #aaa; }
    .score-circle { display: inline-flex; align-items: center; gap: 6px; }
    .score-num { font-size: 28px; font-weight: 700; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>CotIdel — Rapport de conformité NGAP</h1>
      <p style="color:#777;font-size:10px;">Intelligence de cotation infirmière — ${rapportNumber}</p>
      <p style="color:#777;font-size:10px;">Généré le ${now}</p>
    </div>
    <div class="header-right">
      <div class="score-circle">
        <span class="score-num" style="color:${risqueColor}">${result.risque.score}</span>
        <span style="font-size:12px;color:#777;">/100</span>
      </div>
      <span class="badge badge-${result.risque.couleur === 'green' ? 'green' : result.risque.couleur === 'amber' ? 'amber' : 'red'}">${result.risque.niveau}</span>
    </div>
  </div>

  <p style="background:#f0fdf4;padding:8px 12px;border-radius:6px;font-size:10px;color:#166534;margin-bottom:20px;">${result.resumeAnalyse}</p>

  <h2>1. Actes identifiés et cotés</h2>
  <table>
    <tr><th>Code</th><th>Acte</th><th>Coef.</th><th>Passage</th><th>Montant</th></tr>
    ${result.cotations.map((c) => `<tr><td><strong>${c.code}</strong></td><td>${c.label}</td><td>${c.coefficient}</td><td>${c.passage}</td><td><strong>${c.montant.toFixed(2).replace('.', ',')} €</strong></td></tr>`).join('')}
  </table>

  <h2>2. Vérification des cumuls</h2>
  ${result.cumuls.map((c) => `<div class="rule-box ${c.compatible ? 'rule-ok' : 'rule-warn'}"><strong>${c.acte1} + ${c.acte2 !== '—' ? ` ${c.acte2}` : ''}</strong> — ${c.compatible ? '✅ Cumul autorisé' : '⚠️ À vérifier'}<br><span style="color:#555;">${c.regle}</span><br><span style="color:#999;font-size:9px;font-style:italic;">${c.article}</span></div>`).join('')}

  <h2>3. Majorations</h2>
  ${result.majorations.map((m) => `<div class="rule-box ${m.applicable ? 'rule-ok' : ''}"><strong>${m.code} — ${m.nom}</strong> : ${m.applicable ? `✅ ${m.montant}` : '❌ Non applicable'}<br><span style="color:#555;">${m.raison}</span></div>`).join('')}

  <h2>4. Respect des limites quotidiennes</h2>
  <table>
    <tr><th>Code</th><th>Acte</th><th>Limite</th><th>Côté</th><th>Statut</th></tr>
    ${result.limites.map((l) => `<tr><td>${l.code}</td><td>${l.nom}</td><td>${l.limite}</td><td>${l.actuel}</td><td>${l.ok ? '✅ OK' : '⚠️ Dépassé'}</td></tr>`).join('')}
  </table>

  <h2>5. Récapitulatif de facturation</h2>
  <table>
    <tr><td>Sous-total actes</td><td style="text-align:right;"><strong>${result.total.sousTotalActes}</strong></td></tr>
    <tr><td>Majorations</td><td style="text-align:right;color:#b45309;">${result.total.totalMajorations}</td></tr>
    <tr class="total-row"><td>Total</td><td style="text-align:right;color:#2d6a4f;font-size:14px;">${result.total.totalGeneral}</td></tr>
  </table>

  <h2>6. Points d'attention</h2>
  <ul style="padding-left:16px;font-size:10px;line-height:1.8;">
    ${result.avertissements.map((a) => `<li>${a}</li>`).join('')}
  </ul>

  <h2>7. Sources réglementaires</h2>
  <ul style="padding-left:16px;font-size:10px;line-height:1.8;">
    ${result.sources.map((s) => `<li>${s}</li>`).join('')}
  </ul>

  <div class="footer">
    <p>CotIdel — Intelligence de cotation infirmière | Ce rapport est généré à titre d'aide à la cotation. Il ne remplace pas la responsabilité de l'IDEL dans la facturation de ses actes. | Rapport ${rapportNumber} généré le ${now}</p>
    <p style="margin-top:4px;">🔒 Aucune donnée conservée — Rapport généré côté client</p>
  </div>
</body>
</html>`;
  };

  const handleDownload = () => {
    if (downloadingRef.current) return;
    downloadingRef.current = true;

    try {
      const html = generateReportHTML();
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CotIdel_Rapport_Conformite_${rapportNumber.replace('/', '-')}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setTimeout(() => { downloadingRef.current = false; }, 500);
    }
  };

  return (
    <button
      onClick={handleDownload}
      className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
    >
      <i className="ri-download-line"></i>
      Télécharger le rapport de conformité
    </button>
  );
}